import type { CanFrame } from '../canlog/types';
import type { EventMarker } from './eventMarkers';

const BYTE_COUNT = 8;
const EPSILON = 0.001;

export type CorrelationDirection = 'rising' | 'falling' | 'mixed' | 'static';
export type CorrelationConfidence = 'strong' | 'moderate' | 'weak';

export type CorrelationOptions = {
  preWindowMs?: number;
  postWindowMs?: number;
  baselineWindowMs?: number;
  minSamples?: number;
};

export type CorrelationByteResult = {
  id: string;
  canId: number;
  canIdHex: string;
  byteIndex: number;
  score: number;
  changeScore: number;
  meanShift: number;
  direction: CorrelationDirection;
  lagMs: number | null;
  sampleCount: number;
  baselineSamples: number;
  eventSamples: number;
  baselineMean: number;
  eventMean: number;
  baselineVariance: number;
  eventVariance: number;
  entropyNormalized: number;
  changeRatio: number;
  confidence: CorrelationConfidence;
  downrankedReason?: string;
};

export type CorrelationRun = {
  eventId: string;
  results: CorrelationByteResult[];
  warnings: string[];
  windows: {
    baselineStartMs: number;
    baselineEndMs: number;
    eventStartMs: number;
    eventEndMs: number;
  };
};

type ByteSample = {
  t: number;
  value: number;
};

type MessageSamples = {
  canId: number;
  canIdHex: string;
  bytes: ByteSample[][];
};

function mean(values: number[]) {
  return values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;
}

function variance(values: number[], avg = mean(values)) {
  if (values.length < 2) return 0;
  return values.reduce((sum, value) => sum + (value - avg) ** 2, 0) / (values.length - 1);
}

function sampleValues(samples: ByteSample[]) {
  return samples.map((sample) => sample.value);
}

function changeRatio(samples: ByteSample[]) {
  if (samples.length < 2) return 0;
  let changes = 0;
  for (let index = 1; index < samples.length; index += 1) {
    if (samples[index].value !== samples[index - 1].value) changes += 1;
  }
  return changes / (samples.length - 1);
}

function entropyNormalized(samples: ByteSample[]) {
  if (samples.length === 0) return 0;
  const counts = new Map<number, number>();
  for (const sample of samples) counts.set(sample.value, (counts.get(sample.value) ?? 0) + 1);
  const entropy = Array.from(counts.values()).reduce((sum, count) => {
    const p = count / samples.length;
    return sum - p * Math.log2(p);
  }, 0);
  return Math.min(1, entropy / 8);
}

function looksLikeCounter(samples: ByteSample[]) {
  if (samples.length < 8) return false;
  let sequential = 0;
  let wraps = 0;
  for (let index = 1; index < samples.length; index += 1) {
    const previous = samples[index - 1].value;
    const current = samples[index].value;
    if (current === (previous + 1) % 256) sequential += 1;
    if (current < previous) wraps += 1;
  }
  return sequential / (samples.length - 1) > 0.7 || wraps > 2;
}

function directionFor(meanShift: number, eventVar: number, baselineVar: number, eventMean: number, baselineMean: number): CorrelationDirection {
  const signedShift = eventMean - baselineMean;
  const threshold = Math.max(1, Math.sqrt(baselineVar) * 0.35);
  if (Math.abs(signedShift) <= threshold) {
    return eventVar > baselineVar * 1.8 ? 'mixed' : 'static';
  }
  return signedShift > 0 ? 'rising' : 'falling';
}

function lagFor(samples: ByteSample[], eventStartMs: number, baselineMean: number, baselineVariance: number, meanShift: number) {
  const threshold = Math.max(2, Math.sqrt(baselineVariance) * 2, meanShift * 0.45);
  const match = samples.find((sample) => sample.t >= eventStartMs && Math.abs(sample.value - baselineMean) >= threshold);
  return match ? Math.max(0, match.t - eventStartMs) : null;
}

function confidenceFor(score: number, meanShift: number, eventSamples: number): CorrelationConfidence {
  if (score >= 8 && meanShift >= 4 && eventSamples >= 4) return 'strong';
  if (score >= 3.5 && meanShift >= 2 && eventSamples >= 3) return 'moderate';
  return 'weak';
}

function formatCanId(canId: number) {
  return `0x${canId.toString(16).toUpperCase().padStart(canId > 0x7ff ? 8 : 3, '0')}`;
}

function groupSamples(frames: CanFrame[]) {
  const groups = new Map<number, MessageSamples>();
  for (const frame of frames) {
    if (!Number.isFinite(frame.timestampMs)) continue;
    let group = groups.get(frame.canId);
    if (!group) {
      group = {
        canId: frame.canId,
        canIdHex: frame.canIdHex || formatCanId(frame.canId),
        bytes: Array.from({ length: BYTE_COUNT }, () => [])
      };
      groups.set(frame.canId, group);
    }
    for (let byteIndex = 0; byteIndex < BYTE_COUNT; byteIndex += 1) {
      const value = frame.data[byteIndex];
      if (typeof value === 'number') group.bytes[byteIndex].push({ t: frame.timestampMs, value });
    }
  }
  return Array.from(groups.values());
}

export function runEventCorrelation(frames: CanFrame[], event: EventMarker, options: CorrelationOptions = {}): CorrelationRun {
  const preWindowMs = options.preWindowMs ?? 2000;
  const postWindowMs = options.postWindowMs ?? 2000;
  const baselineWindowMs = options.baselineWindowMs ?? 8000;
  const minSamples = options.minSamples ?? 3;
  const eventStartMs = event.timestampMs;
  const eventEndMs = event.timestampMs + Math.max(postWindowMs, event.durationMs ?? 0);
  const primaryBaselineStartMs = event.timestampMs - baselineWindowMs;
  const primaryBaselineEndMs = event.timestampMs - preWindowMs;
  const fallbackBaselineStartMs = event.timestampMs - preWindowMs;
  const fallbackBaselineEndMs = event.timestampMs;
  const warnings: string[] = [];
  const results: CorrelationByteResult[] = [];

  if (frames.length === 0) {
    return {
      eventId: event.id,
      results,
      warnings: ['No parsed frames are available for correlation.'],
      windows: {
        baselineStartMs: primaryBaselineStartMs,
        baselineEndMs: primaryBaselineEndMs,
        eventStartMs,
        eventEndMs
      }
    };
  }

  for (const group of groupSamples(frames)) {
    for (let byteIndex = 0; byteIndex < BYTE_COUNT; byteIndex += 1) {
      const samples = group.bytes[byteIndex];
      if (samples.length < minSamples * 2) continue;

      let baselineSamples = samples.filter((sample) => sample.t >= primaryBaselineStartMs && sample.t < primaryBaselineEndMs);
      if (baselineSamples.length < minSamples) {
        baselineSamples = samples.filter((sample) => sample.t >= fallbackBaselineStartMs && sample.t < fallbackBaselineEndMs);
      }
      const eventSamples = samples.filter((sample) => sample.t >= eventStartMs && sample.t <= eventEndMs);

      if (baselineSamples.length < minSamples || eventSamples.length < minSamples) continue;

      const baselineValues = sampleValues(baselineSamples);
      const eventValues = sampleValues(eventSamples);
      const baselineMean = mean(baselineValues);
      const eventMean = mean(eventValues);
      const baselineVariance = variance(baselineValues, baselineMean);
      const eventVariance = variance(eventValues, eventMean);
      const changeScore = eventVariance / (baselineVariance + EPSILON);
      const meanShift = Math.abs(eventMean - baselineMean);
      const byteChangeRatio = changeRatio(samples);
      const byteEntropyNormalized = entropyNormalized(samples);
      const byteActivity = Math.min(1, byteChangeRatio + (Math.max(...eventValues) - Math.min(...eventValues)) / 255);

      // Ranking intentionally combines the requested variance ratio with
      // mean shift and byte activity. Counter-like bytes are down-ranked, not
      // removed, because they can still be useful evidence during inspection.
      let score = Math.log10(changeScore + 1) * 2.4
        + Math.min(6, meanShift / 7)
        + byteActivity * 1.6
        + Math.min(1.5, eventSamples.length / 20);
      let downrankedReason: string | undefined;
      if (looksLikeCounter(samples)) {
        score *= 0.35;
        downrankedReason = 'possible counter/checksum';
      }

      if (score < 0.8 || meanShift < 1) continue;

      const direction = directionFor(meanShift, eventVariance, baselineVariance, eventMean, baselineMean);
      const lagMs = lagFor(eventSamples, eventStartMs, baselineMean, baselineVariance, meanShift);
      results.push({
        id: `${event.id}-${group.canId}-${byteIndex}`,
        canId: group.canId,
        canIdHex: group.canIdHex,
        byteIndex,
        score,
        changeScore,
        meanShift,
        direction,
        lagMs,
        sampleCount: samples.length,
        baselineSamples: baselineSamples.length,
        eventSamples: eventSamples.length,
        baselineMean,
        eventMean,
        baselineVariance,
        eventVariance,
        entropyNormalized: byteEntropyNormalized,
        changeRatio: byteChangeRatio,
        confidence: confidenceFor(score, meanShift, eventSamples.length),
        downrankedReason
      });
    }
  }

  if (results.length === 0) warnings.push('No strong changes detected. Try a clearer event timestamp or repeat the scenario.');

  return {
    eventId: event.id,
    results: results.sort((a, b) => b.score - a.score).slice(0, 80),
    warnings,
    windows: {
      baselineStartMs: primaryBaselineStartMs,
      baselineEndMs: primaryBaselineEndMs,
      eventStartMs,
      eventEndMs
    }
  };
}
